/**
 * Session detail page with audio player
 */
import { useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Heart, Clock, Lock, Loader2, CheckCircle } from 'lucide-react';
import { Button, Card, CardContent, Alert, AlertDescription, SessionImageFallback } from '@/components/ui';
import { AudioPlayer } from '@/components/features';
import { useLanguage, useIsAuthenticated, useUser } from '@/store';
import {
  useSession,
  useFavorites,
  useToggleFavorite,
  useStartSession,
  useUpdateProgress,
  useCompleteSession,
  useSessionProgress,
} from '@/hooks';
import { cn } from '@/utils/cn';
import { getCategoryLabel } from '@/utils/categories';
import { getSessionDetailTranslation } from '@/utils/translations';
import { ROUTES } from '@/config/routes';
import { hasAccess } from '@/utils/access';

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }
  return `${minutes} min`;
}

export function SessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const language = useLanguage();
  const isAuthenticated = useIsAuthenticated();
  const user = useUser();

  const { data: session, isLoading, error } = useSession(sessionId || '');
  const { data: favorites } = useFavorites();
  const { data: progress } = useSessionProgress(sessionId || '');
  const toggleFavorite = useToggleFavorite();
  const startSession = useStartSession();
  const updateProgress = useUpdateProgress();
  const completeSession = useCompleteSession();

  // Track last reported progress to avoid duplicate updates
  const lastReportedProgress = useRef<number>(-1);

  // Check if session is a favorite
  const isFavorite = favorites?.some((f: { id: string }) => f.id === sessionId) ?? false;

  // Check access permissions
  // Every plan and the trial open premium sessions (R2, R5).
  const showLock = session?.is_premium && !hasAccess(user);

  // Get localized content - sessions are now pre-localized by the hook
  const title = session?.title ?? '';
  const description = session?.description ?? '';
  const benefits = session?.benefits ?? '';
  const categoryLabel = session
    ? getCategoryLabel(session.category, language)
    : '';

  // Handle session start
  const handleStart = useCallback(() => {
    if (sessionId && isAuthenticated) {
      startSession.mutate(sessionId);
    }
  }, [sessionId, isAuthenticated, startSession]);

  // Handle progress update (throttled to every 10%)
  const handleProgress = useCallback(
    (currentTime: number, duration: number) => {
      if (sessionId && isAuthenticated && duration > 0) {
        const progressPercent = Math.floor((currentTime / duration) * 100);
        // Calculate the 10% milestone (10, 20, 30, etc.)
        const milestone = Math.floor(progressPercent / 10) * 10;

        // Only update if we've reached a new milestone and haven't reported it yet
        if (milestone > 0 && milestone !== lastReportedProgress.current) {
          lastReportedProgress.current = milestone;
          updateProgress.mutate({ sessionId, progress: milestone });
        }
      }
    },
    [sessionId, isAuthenticated, updateProgress]
  );

  // Handle session completion
  const handleComplete = useCallback(() => {
    if (sessionId && isAuthenticated) {
      completeSession.mutate(sessionId);
    }
  }, [sessionId, isAuthenticated, completeSession]);

  // Handle favorite toggle
  const handleFavoriteClick = () => {
    if (sessionId && isAuthenticated) {
      toggleFavorite.mutate(sessionId);
    }
  };

  // Opening premium content without a valid session: send the visitor to login. The API client no
  // longer redirects on 401 (background calls must not), so this page handles its own 401.
  const needsLogin =
    (error as { status?: number } | null)?.status === 401 || Boolean(session && showLock && !isAuthenticated);
  useEffect(() => {
    if (needsLogin) {
      // replace: Back must not return to this page, which would only redirect again.
      navigate(`${ROUTES.login}?redirect=${ROUTES.sessionDetail(sessionId ?? '')}`, { replace: true });
    }
  }, [needsLogin, navigate, sessionId]);

  if (isLoading) {
    return (
      <div className="container py-16 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="container py-16">
        <Alert variant="destructive">
          <AlertDescription>
            {getSessionDetailTranslation('loadError', language)}
          </AlertDescription>
        </Alert>
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate(ROUTES.guided)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {getSessionDetailTranslation('backToSessions', language)}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4 sm:py-8">
      {/* Back button */}
      <div className="mb-4 sm:mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.guided)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {getSessionDetailTranslation('backToSessions', language)}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
        {/* Main content */}
        <div className="lg:col-span-2">
          {/* Session header */}
          <div className="mb-4 sm:mb-6">
            {/* Category and duration */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-2">
              <span className="text-xs sm:text-sm font-medium text-primary uppercase tracking-wide">
                {categoryLabel}
              </span>
              <span className="flex items-center text-xs sm:text-sm text-muted-foreground">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                {formatDuration(session.duration)}
              </span>
              {session.is_premium && (
                <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                  Premium
                </span>
              )}
              {progress?.completed && (
                <span className="flex items-center text-xs sm:text-sm text-green-600">
                  <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  {getSessionDetailTranslation('completed', language)}
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">{title}</h1>

            {/* Description */}
            {description && (
              <p className="text-muted-foreground text-base sm:text-lg">{description}</p>
            )}
          </div>

          {/* Audio Player or Lock */}
          {showLock ? (
            <Card className="mb-6">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Lock className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  {getSessionDetailTranslation('premiumContent', language)}
                </h3>
                <p className="text-muted-foreground text-center mb-4">
                  {getSessionDetailTranslation('upgradeToAccess', language)}
                </p>
                <Button asChild>
                  <Link to="/pricing">
                    {getSessionDetailTranslation('viewPricing', language)}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <AudioPlayer
              session={session}
              onStart={handleStart}
              onProgress={handleProgress}
              onComplete={handleComplete}
              className="mb-6"
            />
          )}

          {/* Benefits section */}
          {benefits && (
            <Card>
              <CardContent className="py-6">
                <h2 className="text-xl font-semibold mb-4">
                  {getSessionDetailTranslation('benefits', language)}
                </h2>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  {benefits.split('\n').map((line: string, index: number) => (
                    <p key={index} className="mb-2">
                      {line}
                    </p>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          {/* Session image */}
          {session.image_url ? (
            <div className="rounded-xl overflow-hidden mb-6">
              <img
                src={session.image_url}
                alt={title}
                className="w-full h-48 object-cover"
              />
            </div>
          ) : (
            <SessionImageFallback className="h-48 rounded-xl mb-6" iconClassName="h-12 w-12" />
          )}

          {/* Actions */}
          <div className="space-y-3">
            {isAuthenticated && (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleFavoriteClick}
                disabled={toggleFavorite.isPending}
              >
                <Heart
                  className={cn(
                    'h-4 w-4 mr-2',
                    isFavorite && 'fill-red-500 text-red-500'
                  )}
                />
                {isFavorite
                  ? getSessionDetailTranslation('removeFromFavorites', language)
                  : getSessionDetailTranslation('addToFavorites', language)}
              </Button>
            )}

            {!isAuthenticated && (
              <Alert>
                <AlertDescription>
                  <Link to="/login" className="text-primary hover:underline">
                    {getSessionDetailTranslation('signIn', language)}
                  </Link>{' '}
                  {getSessionDetailTranslation('trackProgressAndSaveFavorites', language)}
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Progress info */}
          {isAuthenticated && progress && !progress.completed && progress.progress > 0 && (
            <div className="mt-6">
              <p className="text-sm text-muted-foreground">
                {getSessionDetailTranslation('yourProgress', language)}
              </p>
              <div className="h-2 bg-muted rounded-full mt-2">
                <div
                  className="h-full bg-primary rounded-full"
                  style={{ width: `${progress.progress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {progress.progress}% {getSessionDetailTranslation('complete', language)}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
